import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Typography, ToggleButton, ToggleButtonGroup } from "@mui/material";

import { StyledBadge, versionSelector, versionSelectorButton } from "./stylesPdfDocumentsList";
import { IDocumentVersion, ISynchronizationItem, ISynchronizationItemMeta } from "utils/DexieDBManager";
import {
  DocumentActionsStatusState,
  useDocumentActionsStatus,
} from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import { DOCUMENT_VERSION_TYPE } from "utils/i18n/resources";

type ChoosenDocumentVersionType = {
  documentVersionType: DOCUMENT_VERSION_TYPE;
  documentVersions: IDocumentVersion[];
  starredDocumentsCount: number;
};

interface IVersionSelectorProps {
  choosenDocumentVersionType: string;
  choosenSynchronizationItem: ISynchronizationItem;
  setDocumentVersionsToDisplay: (synchronizationItem: IDocumentVersion[]) => void;
  setChoosenDocumentVersion: (documentVersion: ChoosenDocumentVersionType) => void;
}

const VersionSelector: React.FunctionComponent<IVersionSelectorProps> = ({
  choosenSynchronizationItem,
  choosenDocumentVersionType,
  setChoosenDocumentVersion,
  setDocumentVersionsToDisplay,
}) => {
  const { pathname } = useLocation();
  const { t: translate } = useTranslation();
  const { state } = useDocumentActionsStatus();

  const handleOnVersionChange = async (documentVersionType: DOCUMENT_VERSION_TYPE) => {
    if (!Boolean(documentVersionType)) {
      return;
    }

    let documentVersions: IDocumentVersion[] = [];
    let starredDocumentsCount = 0;

    if (documentVersionType === DOCUMENT_VERSION_TYPE.APPLICABLES) {
      starredDocumentsCount = choosenSynchronizationItem.starredDocumentsCount?.applicables || 0;
      documentVersions = choosenSynchronizationItem?.applicables ?? [];
    } else {
      starredDocumentsCount = choosenSynchronizationItem.starredDocumentsCount?.nextApplicables || 0;
      documentVersions = choosenSynchronizationItem?.nextApplicables ?? [];
    }

    setChoosenDocumentVersion({
      documentVersions,
      documentVersionType,
      starredDocumentsCount,
    });
    console.info("documentVersions", documentVersions);
    setDocumentVersionsToDisplay(documentVersions.slice(0, 30));
  };

  return (
    <ToggleButtonGroup
      value={choosenDocumentVersionType}
      exclusive
      onChange={(_, value) => handleOnVersionChange(value)}
      aria-label="Document applicable"
      color="secondary"
      size="small"
      sx={versionSelector}
    >
      <ToggleButton value={DOCUMENT_VERSION_TYPE.APPLICABLES} aria-label="Applicable" sx={versionSelectorButton}>
        <Typography fontWeight={600} fontSize={12}>
          {translate(DOCUMENT_VERSION_TYPE.APPLICABLES)}
        </Typography>
        <StyledBadge
          showZero={true}
          badgeContent={computeBadgeContent(
            state,
            choosenSynchronizationItem,
            DOCUMENT_VERSION_TYPE.APPLICABLES,
            pathname,
          )}
          selected={choosenDocumentVersionType === DOCUMENT_VERSION_TYPE.APPLICABLES}
          color="secondary"
        ></StyledBadge>
      </ToggleButton>
      <ToggleButton
        value={DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES}
        aria-label="Prochainement applicable"
        sx={versionSelectorButton}
      >
        <Typography fontWeight={600} fontSize={12}>
          {translate(DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES)}
        </Typography>
        <StyledBadge
          showZero={true}
          badgeContent={computeBadgeContent(
            state,
            choosenSynchronizationItem,
            DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES,
            pathname,
          )}
          selected={choosenDocumentVersionType === DOCUMENT_VERSION_TYPE.NEXT_APPLICABLES}
          color="secondary"
        ></StyledBadge>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default VersionSelector;

function computeBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem,
  documentVersionType: DOCUMENT_VERSION_TYPE,
  pathname: string,
) {
  const isStarredPage = pathname === "/starred";
  const isAcknowledgementPage = pathname === "/acknowledgement";

  if (isStarredPage) {
    return computeStarredBadgeContent(state, synchronizationItem, documentVersionType);
  }

  if (isAcknowledgementPage) {
    return computeAcknowledgementBadgeContent(state, synchronizationItem, documentVersionType);
  }

  return computeCagetoriesBadgeContent(state, synchronizationItem, documentVersionType);
}

function computeCagetoriesBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem,
  documentVersionType: DOCUMENT_VERSION_TYPE,
): React.ReactNode {
  const categoriesIsAckRequiredDocumentsState = state.categoriesIsAckRequiredDocumentsState.get(synchronizationItem.id);

  if (documentVersionType === DOCUMENT_VERSION_TYPE.APPLICABLES) {
    const isAckRequiredApplicableDocumentsCount =
      categoriesIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0;
    const applicablesDocumentsCount = categoriesIsAckRequiredDocumentsState?.applicablesDocumentsCount ?? 0;

    return `${isAckRequiredApplicableDocumentsCount}/${applicablesDocumentsCount}`;
  }

  const isAckRequiredNextApplicableDocumentsCount =
    categoriesIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0;
  const nextApplicablesDocumentsCount = categoriesIsAckRequiredDocumentsState?.nextApplicablesDocumentsCount ?? 0;

  return `${isAckRequiredNextApplicableDocumentsCount}/${nextApplicablesDocumentsCount}`;
}

function computeStarredBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem,
  documentVersionType: DOCUMENT_VERSION_TYPE,
) {
  const starredIsAckRequiredDocumentsState = state.starredIsAckRequiredDocumentsState.get(synchronizationItem.id);

  if (documentVersionType === DOCUMENT_VERSION_TYPE.APPLICABLES) {
    const isAckRequiredApplicableDocumentsCount =
      starredIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0;
    const applicablesDocumentsCount = starredIsAckRequiredDocumentsState?.applicablesDocumentsCount ?? 0;

    return `${isAckRequiredApplicableDocumentsCount}/${applicablesDocumentsCount}`;
  }

  const isAckRequiredNextApplicableDocumentsCount =
    starredIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0;
  const nextApplicableDocumentsCount = starredIsAckRequiredDocumentsState?.nextApplicablesDocumentsCount ?? 0;

  return `${isAckRequiredNextApplicableDocumentsCount}/${nextApplicableDocumentsCount}`;
}

function computeAcknowledgementBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem,
  documentVersionType: DOCUMENT_VERSION_TYPE,
): React.ReactNode {
  const acknowledgementIsAckRequiredDocumentsState = state.acknowledgementIsAckRequiredDocumentsState.get(
    synchronizationItem.id,
  );

  return documentVersionType === DOCUMENT_VERSION_TYPE.APPLICABLES
    ? (acknowledgementIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0)
    : (acknowledgementIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0);
}
